"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useWaterAggregation = void 0;
const react_1 = require("react");
const useAlertChangeRefresh_1 = require("./useAlertChangeRefresh");
const queries_1 = require("../../graphql/queries");
const water_1 = require("../../utils/aggregation/water");
const graphqlCache_1 = require("../../utils/graphqlCache");
const sensorClassification_1 = require("../../utils/sensorClassification");
const stableValue_1 = require("../../utils/stableValue");
const THROTTLE_MS = 5000;
const useWaterAggregation = (hubId, allSensors) => {
    const sensors = allSensors ?? [];
    const [fetchResults, setFetchResults] = (0, react_1.useState)(new Map());
    const [loading, setLoading] = (0, react_1.useState)(true);
    const lastFetchTime = (0, react_1.useRef)(0);
    const intervalRef = (0, react_1.useRef)(null);
    /** Per-sensor "currently flagged for humidity" state, for hysteresis. */
    const elevatedHumidity = (0, react_1.useRef)(new Map());
    const waterSensors = (0, react_1.useMemo)(() => (0, water_1.selectWaterSensors)(sensors), [sensors, hubId]);
    const fetchAllSensorData = (0, react_1.useCallback)(async (force = false) => {
        if (!hubId || waterSensors.length === 0) {
            setFetchResults(new Map());
            setLoading(false);
            return;
        }
        const now = Date.now();
        if (!force && now - lastFetchTime.current < THROTTLE_MS)
            return;
        lastFetchTime.current = now;
        if (force)
            (0, graphqlCache_1.clearGraphqlCache)();
        try {
            const results = await Promise.all(waterSensors.map(async (sensor) => {
                try {
                    const [eventResponse, alertResponse] = await Promise.all([
                        (0, graphqlCache_1.cachedGraphql)({
                            query: queries_1.getSensorEventsBySensor,
                            variables: { sensorId: sensor.id, limit: 5 },
                        }),
                        (0, graphqlCache_1.cachedGraphql)({
                            query: queries_1.getSensorAlertsBySensor,
                            variables: { sensorId: sensor.id, limit: 10 },
                        }),
                    ]);
                    const events = eventResponse.data?.getSensorEventsBySensor?.items ||
                        eventResponse.data?.getSensorEventsBySensor ||
                        [];
                    const alerts = alertResponse.data?.getSensorAlertsBySensor?.items ||
                        alertResponse.data?.getSensorAlertsBySensor ||
                        [];
                    // Find the first DATA event (skip battery-only events that lack values)
                    const dataEvent = events.find((e) => e.values);
                    return {
                        sensorId: sensor.id,
                        event: dataEvent ? (0, sensorClassification_1.parseSensorEvent)(dataEvent) : null,
                        alerts: alerts,
                    };
                }
                catch {
                    return { sensorId: sensor.id, event: null, alerts: [] };
                }
            }));
            const map = new Map();
            results.forEach((r) => map.set(r.sensorId, r));
            setFetchResults((prev) => (0, stableValue_1.stable)(prev, map));
        }
        catch {
            // Keep existing results on error
        }
        finally {
            setLoading(false);
        }
    }, [hubId, waterSensors.map((s) => s.id).join(',')]);
    (0, react_1.useEffect)(() => {
        fetchAllSensorData();
        intervalRef.current = setInterval(() => fetchAllSensorData(), 30_000);
        return () => {
            if (intervalRef.current)
                clearInterval(intervalRef.current);
        };
    }, [fetchAllSensorData]);
    // Refresh immediately when an alert is created or acknowledged so the
    // dashboard "1 leak" badge disappears within a second of the clear,
    // instead of waiting for the next 30s poll.
    const refreshOnAlertChange = (0, react_1.useCallback)(() => {
        fetchAllSensorData(true);
    }, [fetchAllSensorData]);
    (0, useAlertChangeRefresh_1.useAlertChangeRefresh)(refreshOnAlertChange);
    return (0, react_1.useMemo)(() => (0, water_1.aggregateWaterSensors)(waterSensors, fetchResults, loading, elevatedHumidity.current), [waterSensors, fetchResults, loading]);
};
exports.useWaterAggregation = useWaterAggregation;
